import React, { useState, useEffect, useMemo } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";

import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const FieldDayIndicatorSales = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalIndicatorSales, setTotalIndicatorSales] = useState(0);
  const [salesPerProduct, setSalesPerProduct] = useState([]);
  const [salesPerHub, setSalesPerHub] = useState([]);

  const [viewType, setViewType] = useState("product");
  const [chartType, setChartType] = useState("pie"); 

  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedHub, setSelectedHub] = useState("all");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const url = `/api/fieldDayIndicatorAgriPromoter/?orgId=${localStorage.getItem(
      "orgId"
    ) || ""}&userId=${localStorage.getItem("id") || ""}`;

    fetchData({ url }, (res) => {
      setTotalIndicatorSales(res?.totalIndicatorSales || 0);
      setSalesPerProduct(res?.salesPerProduct || []);
      setSalesPerHub(res?.salesPerHub || []);
      setLoading(false);
    });
  }, [fetchData]);

  const filteredProductData = useMemo(() => {
    if (selectedProduct === "all") return salesPerProduct;
    return salesPerProduct.filter(
      (p) => p.productName === selectedProduct
    );
  }, [salesPerProduct, selectedProduct]);

  const filteredHubData = useMemo(() => {
    if (selectedHub === "all") return salesPerHub;
    return salesPerHub.filter(
      (h) => h.hubName === selectedHub
    );
  }, [salesPerHub, selectedHub]);

  let chartDataset = [];
  let displayValue = totalIndicatorSales;

  if (viewType === "product") {
    chartDataset = filteredProductData;
    displayValue = filteredProductData.reduce(
      (sum, r) => sum + (r.totalSales || 0),
      0
    );
  } else {
    chartDataset = filteredHubData;
    displayValue = filteredHubData.reduce(
      (sum, r) => sum + (r.totalSales || 0),
      0
    );
  }

  const noData =
    !loading && !salesPerProduct.length && !salesPerHub.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f1f5f9",
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex items-center mb-4"
          style={{ gap: 10, flexWrap: "wrap" }}
        >
          <Select
            value={viewType}
            style={{ width: 160 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedProduct("all");
              setSelectedHub("all");
            }}
          >
            <Option value="product">Product Wise</Option>
            <Option value="hub">Hub Wise</Option>
          </Select>

          {viewType === "product" && (
            <Select
              value={selectedProduct}
              style={{ width: 220, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedProduct(v)}
            >
              <Option value="all">All Products</Option>
              {salesPerProduct.map((p, i) => (
                <Option key={i} value={p.productName || "Unknown"}>
                  {p.productName || "Unknown Product"}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "hub" && (
            <Select
              value={selectedHub}
              style={{ width: 220, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedHub(v)}
            >
              <Option value="all">All Hubs</Option>
              {salesPerHub.map((h, i) => (
                <Option key={i} value={h.hubName || "Unknown"}>
                  {h.hubName || "Unknown Hub"}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              height: "calc(100% - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <h3 style={{ marginBottom: 4 }}>No Field Day Indicator Sales</h3>
            <p style={{ margin: 0 }}>Try changing organisation / filters.</p>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 16,
                padding: 12,
                background: "#e0f2fe",
                borderRadius: 10,
              }}
            >
              <h1
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: 4,
                }}
              >
                {Number(displayValue || 0).toLocaleString()}
              </h1>
              <p
                style={{
                  fontSize: 16,
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                {viewType === "product"
                  ? selectedProduct === "all"
                    ? "Total Field Day Indicator Sales (All Products)"
                    : `Field Day Indicator Sales — ${selectedProduct}`
                  : selectedHub === "all"
                    ? "Total Field Day Indicator Sales (All Hubs)"
                    : `Field Day Indicator Sales — ${selectedHub}`}
              </p>
            </div>

            <div style={{ height: "100%", overflow: "hidden" }}>
              {chartType === "pie" && (
                <DynamicPieChart
                  data={chartDataset}
                  angleField="totalSales"
                  colorField={
                    viewType === "product" ? "productName" : "hubName"
                  }
                  isMaximized={true}
                />
              )}

              {chartType === "column" && (
                <DynamicColumnChart
                  data={chartDataset}
                  xField={viewType === "product" ? "productName" : "hubName"}
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "hubName"
                  }
                  xAxisTitle={viewType === "product" ? "Product" : "Hub"}
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "bar" && (
                <DynamicBarChart
                  data={chartDataset}
                  xField={viewType === "product" ? "productName" : "hubName"}
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "hubName"
                  }
                  xAxisTitle={viewType === "product" ? "Product" : "Hub"}
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "line" && (
                <DynamicLineChart
                  data={chartDataset}
                  xField={viewType === "product" ? "productName" : "hubName"}
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "hubName"
                  }
                  xAxisTitle={viewType === "product" ? "Product" : "Hub"}
                  yAxisTitle="Sales Amount"
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Field Day Indicator Sales</h3>
            <p style={{ marginTop: 4, fontSize: 13 }}>
              No sales records found
            </p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                // fontSize: 32,
                fontWeight: 700,
                color: "#3d5c26",
                marginBottom: 4,
              }}
            >
              {Number(totalIndicatorSales || 0).toLocaleString()}
            </h1>
            <h3
              style={{
                // fontSize: 16,
                fontWeight: 700,
                margin: 0,
                color: "#3d5c26",
              }}
            >
              Field Day Indicator Sales
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 8,
              right: 18,
              cursor: "pointer",
              fontWeight: 600,
              color: "#3d5c26",
              fontSize: 14,
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default FieldDayIndicatorSales;
